/* prettier-ignore */

import { computed, inject, Injectable, signal } from '@angular/core';
import { Message } from '../interface/message.interface';
import { ChatStyles } from '../interface/chatStyles.interface';
import { generateToken } from '../helpers/helpers';
import { CookieService } from "ngx-cookie-service";
import { TranslateService } from '@ngx-translate/core';
import { GoogleTranslateService } from 'src/app/services';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DataChatService {
  private cookieService: CookieService = inject(CookieService);
  private googleTranslateService = inject(GoogleTranslateService);
  private translateService = inject(TranslateService);

  public name = signal<string>('ChatBot');
  public id = signal('');
  public token = signal<string | undefined>('');
  public messages = signal<Message[]>([]);
  public chatIcon = signal<string>('/assets/imgs/chatbot-avatar.png');
  public icon = signal<string | undefined>('');
  public logo = signal<string | undefined>('');

  public showRecommendedMessages = signal<boolean>(true);
  public recommendedMessages = signal<Record<string, string[]>>(null);
  public recommendedMessagesCurrentLang = computed(() => this.recommendedMessages()?.[this.lang()] ?? []);

  public initialMessage = signal<Record<string, string>>(null);
  public initialMessageCurrentLang = computed(() => this.initialMessage()?.[this.lang()]);
  public abortSignal = signal<AbortController>(new AbortController());
  
  public isStreaming = signal<boolean>(false);
  public chatIsTyping = signal<boolean>(false);
  public isSendingChat = signal<boolean>(false);
  
  public isFullScreen = signal<boolean>(false);
  public canSaveChat = signal<boolean>(false);
  public hasHistory = signal<boolean>(false);
  public showTooltip = signal<boolean>(false);
  public hideChat = signal<boolean>(true);

  public inputText = signal(null)
  public selectedImage = signal<File | null>(null);
  public imagePreviewUrl = signal<string | null>(null);

  // POI Sidebar Logic
  public poiList = signal<any[]>([]);
  public activePoiIndex = signal<number>(0);
  public isPoiSidebarOpen = signal<boolean>(false);

  public allowSendImage = signal(false);
  public allowSendAudio = signal(true);
  public allowProactiveBubbles = signal(true);

  public chatStyles: ChatStyles = {
    header_color: '',
    text_header_color: '',
    user_message_color: '',
    user_text_color: '',
    user_border_color: '',
    user_gradient_1: '',
    bot_message_color: '',
    bot_text_color: '',
    bot_border_color: '',
    bot_gradient_1: '',
    bot_gradient_2: '',
    background_color: '',
    button_color: '',
  };

  private lang = toSignal(
    this.translateService.onLangChange.pipe(
      map(event => event.lang),
      startWith(this.translateService.currentLang)
    ),
    { initialValue: this.translateService.currentLang }
  );

  constructor() {
    this.checkCookies();
   }

  public resetChat() {
    this.messages.set([])
    this.token.set(generateToken());
  }

  public showConnectionErrorMessage() {
    if (this.messages()?.[this.messages()?.length - 1].danger)
      return;

    this.messages.update((prev) => [
      ...prev,
      {
        content: this.translateService.instant('Fallo en la comunicación, vuelve a intentarlo'),
        role: 'assistant',
        show: true,
        danger: true,
      },
    ]);
  }

  public removeErrorMsgs() {
    this.messages.update((prev) =>
      prev.filter((msg) => !msg.danger)
    );
  }
// ----------------------------- CURRENT LANG -----------------------------------
  public getCurrentLang(){
    return this.googleTranslateService.getCookieLang() ?? this.translateService.currentLang;
  }

// -------------------------- COOKIES START CONFIG ------------------------------
  private checkCookies(){
    this.checkAllowSendImageCookie();
    this.checkAllowSendAudioCookie();
    this.checkAllowProactiveBubbles();
  }

  private checkAllowSendImageCookie(){
    const allowSendImage = this.cookieService.get('allowSendImage');
    switch(allowSendImage){
      case 'true': this.allowSendImage.set(true); break;
      case 'false': this.allowSendImage.set(false); break;
    }
  }

  private checkAllowSendAudioCookie(){
    const allowSendAudio = this.cookieService.get('allowSendAudio');
    switch(allowSendAudio){
      case 'true': this.allowSendAudio.set(true); break;
      case 'false': this.allowSendAudio.set(false); break;
    }
  }

  private checkAllowProactiveBubbles(){
    const allowProactiveBubbles = this.cookieService.get('allowProactiveBubbles');
    switch(allowProactiveBubbles){
      case 'true': this.allowProactiveBubbles.set(true); break;
      case 'false': this.allowProactiveBubbles.set(false); break;
    }
  }

}